import { css, cx } from '@emotion/css';
import { Button, ProgressDots } from '@repo/ui/molecules';

import { nanoid } from 'nanoid';
import {
    PropsWithChildren,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import { Dialog, Heading, Modal } from 'react-aria-components';

import './globalCSS';

import { WidgetStudy } from '@repo/types/models';
import { Box, Icon } from '@repo/ui/atoms';
import Steps from './Steps';
import DemoMode from './common/DemoMode';
import MainAppContainer, {
    MainAppContainerProps,
} from './common/MainAppContainer';
import PoweredByUxSignals from './common/PoweredByUxSignals';
import useEffectAfterFirstRender from './hooks/useEffectAfterFirstRender';
import { useTimeout } from './hooks/useTimeout';
import DisqualifyMessage from './steps/DisqualifyMessage';
import { Step } from './steps/Step';
import { ContentStyle } from './steps/stepsStyle';
import type { QuestionOption, StepT } from './steps/types';
import ContextWrapper from './utils/ContextWrapper';
import { useStudyContext } from './utils/StudyContext';
import { useTranslationsContext } from './utils/TranslationsContext';
import { BookingConfirmation, postAnswer } from './utils/fetchData';
import { sendEvent } from './utils/sendEvent';
import { getQueryParams, sendMessageOverWebview } from './utils/utils';

export type WidgetProps = MainAppContainerProps & {
    isInitialQualified?: boolean;
    analyticsId: string;
    demoMode: boolean;
    initialStep?: number;
};

const WrapperStyle = css`
    border-radius: 8px;
    background-color: white;
    border: 1px solid #ddd;
    padding: 0 28px;
    display: flex;
    gap: 12px;
`;

// TODO: add to own package
const getIsQualifiedForNextStep = (stepResponse: StepT): boolean => {
    // The calendar step is always true
    if (
        ['noSuitableTimes', 'calendarStep', 'studyPanel'].includes(
            stepResponse.type,
        )
    ) {
        return true;
    }
    if (typeof stepResponse.answer === 'string') {
        return true;
    }

    if (stepResponse.type === 'checkboxes') {
        const res = (stepResponse.answer ?? []).some((v) =>
            'qualifying' in v ? v.qualifying : false,
        );
        return res;
    } else if (stepResponse.answer && 'qualifying' in stepResponse.answer) {
        return stepResponse.answer.qualifying;
    }

    return false;
};

const WidgetUI = ({
    study: initialStudy,
    analyticsId,
    fullPage,
    demoMode,
    initialStep = 0,
    isInitialQualified = true,
    translation,
}: WidgetProps) => {
    const sid = analyticsId;
    const t = translation;
    const { updateStudy, study } = useStudyContext();
    const [modalOpen, setModalOpen] = useState(false);
    const [isQualified, setIsQualified] = useState(isInitialQualified);
    const [isFinishedInFullPage, setFinishedInFullPage] = useState(false);
    const [answers, setAnswers] = useState<StepT[]>([]);
    const [currentStep, setCurrentStep] = useState(initialStep);
    const sessionId = useRef(sid || nanoid());

    const timer = useTimeout();

    const answerStep = async (step: StepT): Promise<void> => {
        if (!getIsQualifiedForNextStep(step)) {
            sendMessageOverWebview('isDisqualified');
            setIsQualified(false);
        }
        const res = await postAnswer(
            step,
            study.id,
            sessionId.current,
            demoMode,
        );
        if (currentStep === 0) {
            sendEvent(
                { analyticsId, studyId: study.id, event: 'embed.cta.clicked' },
                demoMode,
            );
        }
        if (
            step.type === 'calendarStep' &&
            typeof res === 'object' &&
            'consentId' in res
        ) {
            const { consentId, slotId, ical } =
                res as unknown as BookingConfirmation;

            setAnswers((a) => [...a, { ...step, consentId, slotId, ical }]);
        } else {
            setAnswers((a) => [...a, step]);
        }
    };

    const answerStepAndGoToNext = async (step: StepT): Promise<void> => {
        await answerStep(step);
        setCurrentStep((v) => v + 1);
    };

    useLayoutEffect(() => {
        const firstStep = study.steps[0];
        const answersToFirstQuestion = getQueryParams('answer');

        if (
            answersToFirstQuestion &&
            firstStep &&
            'options' in firstStep &&
            answersToFirstQuestion.length > 0
        ) {
            const rawAnswer = answersToFirstQuestion[0];

            if (typeof rawAnswer === 'string') {
                if (
                    firstStep.type === 'multipleChoice' ||
                    firstStep.type === 'statement'
                ) {
                    const answer = firstStep.options[parseInt(rawAnswer, 10)];
                    answerStep({ ...firstStep, answer });
                    if (!getIsQualifiedForNextStep({ ...firstStep, answer })) {
                        setIsQualified(false);
                    } else {
                        setCurrentStep((v) => v + 1);
                    }
                } else if (firstStep.type === 'checkboxes') {
                    const answers: QuestionOption[] = [];
                    answersToFirstQuestion.forEach((answer) => {
                        const option = firstStep.options[parseInt(answer, 10)];
                        if (option) {
                            answers.push(option);
                        }
                    });

                    answerStep({ ...firstStep, answer: answers });
                    if (
                        !getIsQualifiedForNextStep({
                            ...firstStep,
                            answer: answers,
                        })
                    ) {
                        setIsQualified(false);
                    } else {
                        setCurrentStep((v) => v + 1);
                    }
                }
            }
        }
    }, []);

    const closeDialog = () => {
        setModalOpen(false);
        timer.current = setTimeout(() => {
            setCurrentStep(0);
            setAnswers([]);
            sessionId.current = nanoid();
            setIsQualified(true);
            sendMessageOverWebview('closeWidget');
            // just a small delay so that the content of the modal is the same until its closed
        }, 500);
        if (fullPage) {
            setFinishedInFullPage(true);
            window.close();
        }
    };

    useEffect(() => {
        updateStudy(initialStudy);
    }, [initialStudy]);

    useEffectAfterFirstRender(() => {
        setCurrentStep(initialStep);
    }, [initialStep]);

    if (study.steps[0]) {
        return (
            <ContextWrapper
                fullPage={fullPage}
                study={study}
                analyticsId={analyticsId}
                close={closeDialog}
                demoMode={demoMode}
                sessionId={sessionId.current}
            >
                {fullPage ? (
                    <article
                        className={noFocus({
                            isPreview: analyticsId === 'preview',
                        })}
                        tabIndex={-1}
                        aria-label={
                            t('progress.ariaLabel', {
                                current: currentStep + 1,
                                stepCount: study.steps.length + 1,
                            }) as string
                        }
                    >
                        <StepContainer
                            className={ContentStyle}
                            currentStep={currentStep}
                            demoMode={demoMode}
                            study={study}
                        >
                            {isFinishedInFullPage || !isQualified ? (
                                <div
                                    className={cx(ContentStyle, center)}
                                    lang={study.language}
                                >
                                    <DisqualifyMessage />
                                    <PoweredByUxSignals />
                                </div>
                            ) : (
                                <Steps
                                    currentStep={currentStep}
                                    answerStep={answerStepAndGoToNext}
                                    answers={answers}
                                />
                            )}
                        </StepContainer>
                    </article>
                ) : (
                    <div
                        className={cx(WrapperStyle, 'uxsignals-container')}
                        lang={study.language}
                    >
                        <div className="uxsignals-embed">
                            <Box
                                justifyContent="space-between"
                                gap="12"
                                alignItems="baseline"
                                wrap="nowrap"
                            >
                                <Step
                                    step={study.steps[0]}
                                    answerStep={async (v) => {
                                        await answerStepAndGoToNext(v);
                                        setModalOpen(true);
                                    }}
                                />
                                {demoMode && (
                                    <div>
                                        <DemoMode />
                                    </div>
                                )}
                            </Box>
                        </div>

                        {/* // if you want make sure it close when you press outside it breaks the dropdown of adding to cal */}
                        <Modal isOpen={modalOpen} onOpenChange={closeDialog}>
                            <Dialog
                                aria-label={
                                    t('progress.ariaLabel', {
                                        current: currentStep + 1,
                                        stepCount: study.steps.length + 1,
                                    }) as string
                                }
                                className={cx(
                                    embedWidgetContainer,
                                    'uxsignals-container',
                                )}
                            >
                                <Heading hidden slot="title">
                                    UX Signals Widget
                                </Heading>
                                <StepContainer
                                    className={ContentStyle}
                                    currentStep={currentStep}
                                    demoMode={demoMode}
                                    study={study}
                                    closeDialog={closeDialog}
                                >
                                    {isQualified ? (
                                        <Steps
                                            currentStep={currentStep}
                                            answerStep={answerStepAndGoToNext}
                                            answers={answers}
                                        />
                                    ) : (
                                        <>
                                            <DisqualifyMessage />
                                            <PoweredByUxSignals />
                                        </>
                                    )}
                                </StepContainer>
                            </Dialog>
                        </Modal>
                    </div>
                )}
            </ContextWrapper>
        );
    } else {
        return <div />;
    }
};

export function Widget(props: WidgetProps) {
    return (
        <MainAppContainer
            {...props}
            shouldExcludeFont={
                props.analyticsId === 'preview' || !props.fullPage
            }
        >
            <WidgetUI {...props} />
        </MainAppContainer>
    );
}

function StepContainer({
    demoMode,
    study,
    currentStep,
    children,
    closeDialog,
    className,
}: PropsWithChildren<{
    className?: string;
    demoMode: boolean;
    study: WidgetStudy;
    currentStep: number;
    closeDialog?: () => void;
}>) {
    const { t } = useTranslationsContext();
    return (
        <div
            className={cx(StepContainerStyle, className)}
            lang={study.language}
        >
            <Box justifyContent="space-between" width="100%">
                <ProgressDots
                    steps={study.steps.length}
                    currentStep={currentStep}
                    demoMode={demoMode}
                />
                <Box gap="8">
                    {demoMode && <DemoMode />}
                    {closeDialog && (
                        <Button
                            ariaLabel={t('common.close')}
                            onClick={closeDialog}
                            variant="ghost"
                        >
                            <Icon name="exit" />
                        </Button>
                    )}
                </Box>
            </Box>
            {children}
        </div>
    );
}

const StepContainerStyle = css`
    overflow-wrap: break-word;
`;

const embedWidgetContainer = css`
    max-height: 100vh;
    overflow: auto;
    background-color: white;
    border-radius: 12px;
    position: relative;
`;

const noFocus = ({ isPreview }: { isPreview?: boolean }) => css`
    width: ${isPreview ? '100%' : 'auto'};
    :focus,
    :active {
        outline: none;
    }
`;

const center = css`
    align-items: center;
`;
